import {
    FC,
    memo,
    useCallback,
    useState,
} from 'react';

import { Button, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '@/components/hooks';
import { BASEURL, ENDPOINTS } from '@/api';
import { captureErrorForSentry } from '@/components/utils';

import PasswordView from './PasswordView';
import PasswordEdit from '@/components/Common/PasswordEdit';

import styles from './Password.module.scss';

type PasswordViewMode = 'showButton' | 'display' | 'edit';

const nameSpace = 'userRoleEditor.password';

interface PasswordProps {
    email: string;
    userId: string;
}

const Password: FC<PasswordProps> = memo(observer(({ email }) => {
    const { t } = useTranslation();
    const { authSettingsStore: { API } } = useStores();

    const [viewMode, setViewMode] = useState<PasswordViewMode>('showButton');
    const [password, setPassword] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchPassword = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        try {
            const response = await API.get(
                BASEURL.backend(),
                ENDPOINTS.getUserPassword(email),
                {},
            );
            if (response?.password) {
                setPassword(response.password);
            } else {
                setPassword(null);
            }
            setViewMode('display');
        } catch (error: unknown) {
            // 404 means no password set - not an error
            const axiosError = error as { response?: { status?: number } };
            if (axiosError?.response?.status !== 404) {
                captureErrorForSentry(error, 'Password.fetchPassword');
            }
            setPassword(null);
            setViewMode('display');
        } finally {
            setIsLoading(false);
        }
    }, [API, email]);

    const handleShowPassword = useCallback(async (): Promise<void> => {
        await fetchPassword();
    }, [fetchPassword]);

    const handleSavePassword = useCallback(async (newPassword: string): Promise<void> => {
        setIsLoading(true);
        try {
            await API.post(
                BASEURL.backend(),
                ENDPOINTS.setUserPassword(email),
                { body: { password: newPassword } },
            );
            setPassword(newPassword);
            setViewMode('display');
            message.success(t(`${nameSpace}.updateSuccess`));
        } catch (error) {
            captureErrorForSentry(error, 'Password.handleSavePassword');
            message.error(t(`${nameSpace}.updateError`));
        } finally {
            setIsLoading(false);
        }
    }, [API, email, t]);

    const handleCancelEdit = useCallback((): void => {
        setViewMode('display');
    }, []);

    const handleStartEdit = useCallback((): void => {
        setViewMode('edit');
    }, []);

    const renderContent = (): JSX.Element => {
        if (isLoading) {
            return <LoadingOutlined className={styles.loader} spin />;
        }

        switch (viewMode) {
            case 'showButton':
                return (
                    <Button
                        className={styles['show-button']}
                        onClick={handleShowPassword}
                    >
                        {t(`${nameSpace}.show`)}
                    </Button>
                );
            case 'display':
                return (
                    <PasswordView
                        maskedPassword={password}
                        onEdit={handleStartEdit}
                    />
                );
            case 'edit':
                return (
                    <PasswordEdit
                        onSave={handleSavePassword}
                        onCancel={handleCancelEdit}
                        isLoading={isLoading}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className={styles['password-container']}>
            {renderContent()}
        </div>
    );
}));

export default Password;
